import React, {ReactElement, ReactNode} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as DeclarationsIcon} from 'assets/svg/declarations.svg';
import {ReactComponent as ActivityLogIcon} from 'assets/svg/menu-activity-log.svg';
import {ReactComponent as PieChartIcon} from 'assets/svg/menu-dashboard.svg';
import {ReactComponent as DocumentIcon} from 'assets/svg/menu-documents.svg';
import {ReactComponent as BookmarkIcon} from 'assets/svg/menu-orders.svg';
import {ReactComponent as ProductsIcon} from 'assets/svg/menu-package.svg';
import {ReactComponent as RegistriesIcon} from 'assets/svg/menu-products.svg';
import {ReactComponent as ReportsIcon} from 'assets/svg/menu-reports.svg';
import {ReactComponent as TracingIcon} from 'assets/svg/menu-tracing.svg';
import {ReactComponent as WarehouseIcon} from 'assets/svg/menu-warehouse.svg';
import {ReactComponent as ProfileIcon} from 'assets/svg/user.svg';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {UserPermission, UserPermissionValue} from 'types';

import {REPORTS_LINK} from './constants/reports';

export enum NavigationLinksShowEnum {
    desktop = 'desktop',
    mobile = 'mobile',
    all = 'all',
}

export interface LinkData {
    route: ROUTE;
    title: ReactNode;
    icon: ReactElement;
    showOn: NavigationLinksShowEnum;
    permissions?: UserPermissionValue[];
    onClick?: (e?: any) => void;
}

const Title = ({text}: {text: string}) => {
    const {t} = useTranslation();
    /*
        t('menu.Dashboard')
        t('menu.Orders')
        t('menu.Products')
        t('menu.Documents')
        t('menu.Profile')
        t('menu.Registries')
        t('menu.reports')
        t('menu.virtualWarehouse')
        t('menu.activityLog')
        t('menu.Complaints')
    */
    return <span>{t(text)}</span>;
};

export const NAVIGATION_LINKS: LinkData[] = [
    {
        route: ROUTE.COMPLAINTS,
        title: <Title text="menu.Complaints" />,
        icon: <DocumentIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
        permissions: [UserPermission.APPEAL_ALL_LIST, UserPermission.APPEAL_LIST],
    },
    {
        route: ROUTE.DASHBOARD,
        title: <Title text="menu.Dashboard" />,
        icon: <PieChartIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.ORDERS_GRID,
        title: <Title text="menu.Orders" />,
        icon: <BookmarkIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.PRODUCTS_GRID,
        title: <Title text="menu.Products" />,
        icon: <ProductsIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.TRACING,
        title: <Title text="menu.Tracing" />,
        icon: <TracingIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.DOCUMENTS_DOCUMENTS,
        title: <Title text="menu.Documents" />,
        icon: <DocumentIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.VIRTUAL_WAREHOUSE,
        title: <Title text="menu.virtualWarehouse" />,
        icon: <WarehouseIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.REPORTS,
        title: <Title text="menu.reports" />,
        icon: <ReportsIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
        onClick: (e: any) => {
            e.preventDefault?.();
            window.open(REPORTS_LINK, '_blank');
        },
    },
    {
        route: ROUTE.REGISTRIES_GTIN,
        title: <Title text="menu.Registries" />,
        icon: <RegistriesIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.RECALL_AUDIT,
        title: <Title text="menu.activityLog" />,
        icon: <ActivityLogIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
        permissions: [
            PERMISSION_ALIASES.recallAuditLogView,
            PERMISSION_ALIASES.productCardAuditLogView,
            PERMISSION_ALIASES.returnAuditLogView,
            PERMISSION_ALIASES.withdrawalAuditLogView,
            PERMISSION_ALIASES.blockingAuditLogView,
            PERMISSION_ALIASES.remarkingAuditLogView,
            PERMISSION_ALIASES.customsAuditLogView,
        ],
    },
    {
        route: ROUTE.PROFILE,
        title: <Title text="menu.Profile" />,
        icon: <ProfileIcon width="17" height="17" />,
        showOn: NavigationLinksShowEnum.mobile,
    },
    // ...(IS_DEVELOPMENT
    //     ? [
    //           {
    //               route: ROUTE.DECLARATIONS,
    //               title: <Title text="menu.Declarations" />,
    //               icon: <DeclarationsIcon width="24" height="24" />,
    //               showOn: NavigationLinksShowEnum.all,
    //           },
    //       ]
    //     : []),
];

export const NK_NAVIGATION_LINKS = NAVIGATION_LINKS.filter((link) => {
    const ALLOWED_ROUTES = [
        ROUTE.COMPLAINTS,
        ROUTE.RECALL_AUDIT,
        ROUTE.DOCUMENTS_DOCUMENTS,
        ROUTE.PRODUCTS_GRID,
    ];
    return ALLOWED_ROUTES.includes(link.route);
});

export const CUSTOMS_NAVIGATION_LINKS: LinkData[] = [
    {
        route: ROUTE.RECALL_AUDIT,
        title: <Title text="menu.activityLog" />,
        icon: <ActivityLogIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
        permissions: [
            PERMISSION_ALIASES.recallAuditLogView,
            PERMISSION_ALIASES.productCardAuditLogView,
            PERMISSION_ALIASES.returnAuditLogView,
            PERMISSION_ALIASES.withdrawalAuditLogView,
            PERMISSION_ALIASES.blockingAuditLogView,
            PERMISSION_ALIASES.remarkingAuditLogView,
            PERMISSION_ALIASES.customsAuditLogView,
        ],
    },
    {
        route: ROUTE.DECLARATIONS,
        title: <Title text="menu.Declarations" />,
        icon: <DeclarationsIcon width="24" height="24" />,
        showOn: NavigationLinksShowEnum.all,
    },
    {
        route: ROUTE.PROFILE,
        title: <Title text="menu.Profile" />,
        icon: <ProfileIcon width="17" height="17" />,
        showOn: NavigationLinksShowEnum.mobile,
    },
];
